import { DOCUMENT, inject, Injectable } from '@angular/core';
import { RmaEnvironment, RmaTenant } from '@rma/generic/ng/instance';
import { CurrentUserService } from '@rma/generic/ng/user';
import { PlatformService } from '@rma/generic/ng/util';
import posthog, { type CaptureOptions, type PostHogConfig } from 'posthog-js';
import { ReplaySubject } from 'rxjs';
import { PostHogApplication } from './posthog-application';

declare global {
  interface Window {
    posthog?: typeof posthog;
  }
}

/** An undefined Octopus variable arrives as a truthy `#{...}` placeholder, not an empty string. */
function isConfigured(value: string | undefined): value is string {
  return !!value && !value.startsWith('#{');
}

@Injectable({ providedIn: 'root' })
export class PostHogService {
  private readonly platform = inject(PlatformService);
  private readonly document = inject(DOCUMENT);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly tenant = inject(RmaTenant);
  private readonly environment = inject(RmaEnvironment);

  private initialized = false;
  private identifiedUserId: string | undefined;
  private readonly settledSubject = new ReplaySubject<void>(1);

  /**
   * Emits once the SDK has either started or been declined, replaying for anything built later.
   * Consumers exist before the initialiser runs, so one reading `client` directly wires nothing.
   */
  readonly settled$ = this.settledSubject.asObservable();

  get client(): typeof posthog | undefined {
    return this.platform.isPlatformBrowser && this.initialized ? posthog : undefined;
  }

  /** For a crawler or a server render, so nothing waits on a decision already made. */
  markUnavailable(): void {
    this.settle();
  }

  init(apiKey: string | undefined, application: PostHogApplication, options: Partial<PostHogConfig>): void {
    if (!this.platform.isPlatformBrowser || this.initialized || !isConfigured(apiKey) || !isConfigured(options.api_host)) {
      this.settle();

      return;
    }

    posthog.init(apiKey, options);

    posthog.register({
      application,
      tenant: this.tenant.code,
      environment: this.environment.code,
    });

    this.initialized = true;
    this.exposeToConsole();
    this.settle();
    this.initialiseUserIdentification();
  }

  /**
   * Only posthog-js's CDN snippet build assigns this global; the module build imported here never
   * does, and PostHog's console troubleshooting steps all read it. No view means a server render,
   * so there is nothing to expose it on.
   */
  private exposeToConsole(): void {
    const view = this.document.defaultView;

    if (!view) {
      return;
    }

    view.posthog = posthog;
  }

  private settle(): void {
    this.settledSubject.next();
    // Completed, so a second settle is a no-op and both callers can reach it unconditionally.
    this.settledSubject.complete();
  }

  /**
   * The one send path, so every caller goes through the gate on `client`.
   *
   * `options` must reach posthog-js absent rather than as an empty object: it reads any object at
   * all as an opt-out of request batching, so a defaulted `{}` would send every event instantly.
   */
  capture(event: string, properties?: Record<string, unknown>, options?: CaptureOptions): void {
    const client = this.client;

    if (!client) {
      return;
    }

    try {
      client.capture(event, properties, options);
    } catch (error) {
      // A failed send must never interrupt the flow it is measuring.
      console.error(error);
    }
  }

  private initialiseUserIdentification(): void {
    this.currentUserService.currentUser$.subscribe((user) => {
      // Impersonation stays anonymous, or staff activity lands in the customer's funnels and
      // experiments. Heap and Hotjar skip it the same way.
      if (!user?.userId || user.isImpersonating) {
        if (this.identifiedUserId) {
          posthog.reset();
          this.identifiedUserId = undefined;
        }
        return;
      }

      if (this.identifiedUserId === user.userId) {
        return;
      }

      if (this.identifiedUserId) {
        posthog.reset();
      }

      posthog.identify(user.userId, {
        email: user.email,
        name: user.name ?? user.alias,
        rma_user_type: user.userType,
      });
      this.identifiedUserId = user.userId;
    });
  }
}
